import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Hours, RouteLink, Tooltip, Widget } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useHoursFormat, useTimeEntriesReportURL } from '~/hooks';
import { dateFormats } from '~/utils';
import BudgetTooltip from '../components/BudgetTooltip';
import ForecastedBudgetHoursTooltip from '../components/ForecastedBudgetHoursTooltip';
import {
  Box,
  Boxes,
  ChartContainer,
  Label,
  LowerBoxes,
  Metric,
  SmallBox,
  UpperBox,
  Value,
} from '../components/StyledComponents';
import ViewNav from '../components/ViewNav';
import HoursByMonthChart from './HoursByMonthChart';
import HoursProgressChart from './HoursProgressChart';

export default function HoursWidget({ project, view, actualsThrough, onViewChange }) {
  const { workspace } = useWorkspace();

  const api = useApi();
  const [{ data, isReady }, setQuery] = useState({ data: null, isReady: false });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .dashboard()
        .progress.hours({ actualsThrough });
      setQuery({ data, isReady: true });
    } catch (error) {
      setQuery({ data: null, isReady: true });
    }
  }, [workspace.id, project.id, api, actualsThrough]);

  useEffect(() => {
    fetchData();
  }, [fetchData, project]);

  const timeDetail = useTimeEntriesReportURL({
    query: {
      start: 'not_set',
      end: moment(actualsThrough).format(dateFormats.isoDate),
      projectId: project.id,
      currency: project.currency,
    },
  });

  const url = {
    total: timeDetail(),
    billable: timeDetail({ timeBillableTypeId: 'billable' }),
    non_billable: timeDetail({ timeBillableTypeId: project.client.isInternal ? 'internal' : 'non_billable' }),
  };

  const boxes = {
    budget: !!data?.budget,
    forecast: data?.forecast?.dates.length > 0,
    get any() {
      return this.budget || this.forecast;
    },
  };

  const hoursFormat = useHoursFormat({ minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return (
    <Widget loading={!isReady}>
      <Widget.Preview style={{ minHeight: '32.4rem' }} />
      <Widget.Content>
        <ViewNav view={view} onViewChange={onViewChange} />

        {data && (
          <>
            <ChartContainer>
              {data &&
                {
                  progress: () => <HoursProgressChart project={project} data={data} />,
                  month: () => <HoursByMonthChart project={project} data={data} />,
                }[view]()}
            </ChartContainer>
            <Boxes>
              {project.isBillable && (
                <Box>
                  <UpperBox>
                    <RouteLink to={url.billable}>
                      <Value>
                        <Hours value={data.actual.billable} minimumFractionDigits={0} />
                      </Value>
                      <Label>Actual Billable Hours</Label>
                    </RouteLink>
                  </UpperBox>

                  {boxes.any && (
                    <LowerBoxes>
                      {boxes.budget && (
                        <SmallBox>
                          <Metric>
                            <Value>
                              <Hours value={data.budget.billable.estimated ?? 0} minimumFractionDigits={0} />
                            </Value>
                            <Label>Budget</Label>
                          </Metric>
                          <Metric status={data.budget.billable.status}>
                            <BudgetTooltip value={data.budget.billable.percent}>
                              <Value>
                                <Hours value={data.budget.billable.remaining ?? 0} minimumFractionDigits={0} />
                              </Value>
                              <Label>Budget Left</Label>
                            </BudgetTooltip>
                          </Metric>
                        </SmallBox>
                      )}

                      {boxes.forecast && (
                        <SmallBox>
                          <Metric>
                            <Tooltip
                              message={`${hoursFormat.format(data.actual.billable)} actual hours plus ${hoursFormat.format(data.forecast.billable.left)} remaining plan hours.`}
                              placement="top">
                              <Value>
                                <Hours value={data.forecast.billable.forecasted} minimumFractionDigits={0} />
                              </Value>
                              <Label>Forecast</Label>
                            </Tooltip>
                          </Metric>
                          <Metric status={data.forecast.billable.status} data-testid="forecasted-left-billable">
                            <ForecastedBudgetHoursTooltip value={data.forecast.billable}>
                              <Value>
                                <Hours value={data.forecast.billable.left} minimumFractionDigits={0} />
                              </Value>
                              <Label>Plan Left</Label>
                            </ForecastedBudgetHoursTooltip>
                          </Metric>
                        </SmallBox>
                      )}
                    </LowerBoxes>
                  )}
                </Box>
              )}

              <Box>
                <UpperBox>
                  <RouteLink to={url.non_billable}>
                    <Value>
                      <Hours value={data.actual.nonBillable} minimumFractionDigits={0} />
                    </Value>
                    <Label>Actual Non-billable Hours</Label>
                  </RouteLink>
                </UpperBox>

                {boxes.any && (
                  <LowerBoxes>
                    {boxes.budget && (
                      <SmallBox>
                        <Metric>
                          <Value>
                            <Hours value={data.budget.nonBillable.estimated ?? 0} minimumFractionDigits={0} />
                          </Value>
                          <Label>Budget</Label>
                        </Metric>
                        <Metric status={data.budget.nonBillable.status}>
                          <BudgetTooltip value={data.budget.nonBillable.percent}>
                            <Value>
                              <Hours value={data.budget.nonBillable.remaining ?? 0} minimumFractionDigits={0} />
                            </Value>
                            <Label>Budget Left</Label>
                          </BudgetTooltip>
                        </Metric>
                      </SmallBox>
                    )}

                    {boxes.forecast && (
                      <SmallBox>
                        <Metric>
                          <Tooltip
                            message={`${hoursFormat.format(data.actual.nonBillable)} actual hours plus ${hoursFormat.format(data.forecast.nonBillable.left)} remaining plan hours.`}
                            placement="top">
                            <Value>
                              <Hours value={data.forecast.nonBillable.forecasted} minimumFractionDigits={0} />
                            </Value>
                            <Label>Forecast</Label>
                          </Tooltip>
                        </Metric>
                        <Metric status={data.forecast.nonBillable.status} data-testid="forecasted-left-non-billable">
                          <ForecastedBudgetHoursTooltip value={data.forecast.nonBillable}>
                            <Value>
                              <Hours value={data.forecast.nonBillable.left} minimumFractionDigits={0} />
                            </Value>
                            <Label>Plan Left</Label>
                          </ForecastedBudgetHoursTooltip>
                        </Metric>
                      </SmallBox>
                    )}
                  </LowerBoxes>
                )}
              </Box>

              <Box>
                <UpperBox>
                  <RouteLink to={url.total}>
                    <Value>
                      <Hours value={data.actual.total} minimumFractionDigits={0} />
                    </Value>
                    <Label>Actual Total Hours</Label>
                  </RouteLink>
                </UpperBox>

                {boxes.any && (
                  <LowerBoxes>
                    {boxes.budget && (
                      <SmallBox>
                        <Metric>
                          <Value>
                            <Hours value={data.budget.total.estimated ?? 0} minimumFractionDigits={0} />
                          </Value>
                          <Label>Budget</Label>
                        </Metric>
                        <Metric status={data.budget.total.status}>
                          <BudgetTooltip value={data.budget.total.percent}>
                            <Value>
                              <Hours value={data.budget.total.remaining ?? 0} minimumFractionDigits={0} />
                            </Value>
                            <Label>Budget Left</Label>
                          </BudgetTooltip>
                        </Metric>
                      </SmallBox>
                    )}

                    {boxes.forecast && (
                      <SmallBox>
                        <Metric>
                          <Tooltip
                            message={`${hoursFormat.format(data.actual.total)} actual hours plus ${hoursFormat.format(data.forecast.total.left)} remaining plan hours.`}
                            placement="top">
                            <Value>
                              <Hours value={data.forecast.total.forecasted} minimumFractionDigits={0} />
                            </Value>
                            <Label>Forecast</Label>
                          </Tooltip>
                        </Metric>
                        <Metric status={data.forecast.total.status} data-testid="forecasted-left-total">
                          <ForecastedBudgetHoursTooltip value={data.forecast.total}>
                            <Value>
                              <Hours value={data.forecast.total.left} minimumFractionDigits={0} />
                            </Value>
                            <Label>Plan Left</Label>
                          </ForecastedBudgetHoursTooltip>
                        </Metric>
                      </SmallBox>
                    )}
                  </LowerBoxes>
                )}
              </Box>
            </Boxes>
          </>
        )}
      </Widget.Content>
    </Widget>
  );
}
